/* eslint-disable react/prop-types */
/**
 * LeaseDeedsTab — hierarchical master/detail for lease_deeds on a deal.
 *
 * The Lease/Deeds master table drives selection: clicking a row makes that
 * lease/deed the "active" one and loads its Recordings + Tracts below.
 * Editing a lease/deed is via the row's Edit (pencil) button, which opens a
 * full-screen drawer — so row-click stays free for master/detail selection
 * (no separate "active lease/deed" dropdown needed).
 *
 * The master table + edit drawer are composed here from the library's
 * exported primitives (`metadataToAntdColumns`, `WigMetadataForm`,
 * `fromFormValues`) rather than `WigMetadataCrudPanel`, because the panel
 * wires row-click to its edit modal and gives no hook for master/detail
 * selection. The sub-grids (Recordings, Tracts) still use the panel.
 */
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Drawer,
  Empty,
  Form,
  Popconfirm,
  Segmented,
  Space,
  Typography,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
} from "@ant-design/icons";

import {
  WigAntTable,
  WigMetadataCrudPanel,
  WigMetadataForm,
  fromFormValues,
  metadataToAntdColumns,
} from "@warwick-investment-group/wig-react-library";

import { AppContext } from "../../../AppContextProvider";
import usePermissions from "../../../hooks/usePermissions";
import { deriveLeaseDates } from "../../../utils/leaseTermDates";
import {
  applyFieldEditability,
  stripNonWritableFields,
} from "../../../utils/applyFieldEditability";
import {
  brokerHiddenFieldSet,
  isExternalBrokerAgent,
} from "../../../utils/brokerHiddenFields";
import dealsService from "../../../services/dealsService";
import ReadOnlyChildTable from "./ReadOnlyChildTable";
import AttachmentsScopeButton from "./AttachmentsScopeButton";
import { fanOutChildren } from "./dealWideChildren";
import {
  applyNumericFormatters,
  numericCellRenderers,
} from "../../../utils/metadataV2Columns";
import { countyFieldRenderer } from "../../../components/countySelect";
import { TRS_FIELD_RENDERERS } from "../../../components/trsSelect";
import {
  INTEREST_FIELD_RENDERERS,
  withShareBoundsNotice,
} from "../../../components/interestInput";
import { stripReadOnlyForUpdate } from "../../../utils/entityUpdateBody";
import { apiErrorText, saveErrorText } from "../../../utils/apiError";

const { Text } = Typography;

// Strip parent-driven fields from metadata before passing — the library
// panel has no `hideFields` prop, so pre-filtering is the supported way to
// suppress grid + form rows.
const HIDDEN_LEASE_DEED_FIELDS = new Set(["deal_id"]);

// Columns hidden from an EXTERNAL brokerage entirely — grid and drawer alike.
// `lease_cost` ("Lease Cost / Allocated Value") is WIG's own allocation of the
// purchase price against the lease, so a BD.BrokerAgent must not see the
// number at all. That is stricter than the RESERVED columns
// (`land_system_lease_id`, `lease_integrated_date`), which stay visible and
// merely render read-only — hence a separate set rather than another
// `editable_for_me` entry. UI-only: `GET /lease_deeds` still returns the
// column, so this is presentation, not a data boundary.
//
// Sourced from `utils/brokerHiddenFields` rather than written out here: the
// same policy covers `deals.promote*` and `wells.allocated_value`, and the
// server-side half still has to be built (see
// `uploadspec/BDA_Update_Loader_Build_Spec.md` §3.1), so the list needs one
// home rather than four.
const BROKER_HIDDEN_LEASE_DEED_FIELDS = brokerHiddenFieldSet("lease_deeds");
const HIDDEN_RECORDING_FIELDS = new Set(["lease_deed_id"]);
const HIDDEN_TRACT_FIELDS = new Set(["lease_deed_id"]);

// Server-managed / non-editable columns to strip from the edit form — mirrors
// WigMetadataCrudPanel's built-in default form filter so our hand-rolled
// master editor shows the same field set.
const SERVER_MANAGED_FIELDS = new Set([
  "id",
  "created_at",
  "created_by",
  "updated_at",
  "updated_by",
]);

const leaseDeedLabel = (ld) => {
  if (!ld) return "";
  const num = ld.external_number || `#${ld.id}`;
  const grantor = ld.lessor_grantor ? ` — ${ld.lessor_grantor}` : "";
  return `${num}${grantor}`;
};

// `onDealDerivedChange` — called after any write that moves the deal's derived
// `deal_net_acres_delivered` (the sum of tract `co_net_acres_delivered`, rolled
// up by the de-hub trigger in migration 081). The parent re-fetches the deal
// row so the header and Overview stop showing the pre-edit acreage. Optional:
// the tab works without it, the number just lags until the page reloads.
const LeaseDeedsTab = ({ dealId, onDealDerivedChange }) => {
  const { token, getToken, isTokenExpired, user } = useContext(AppContext);
  // The two panels have DIFFERENT server guards, so they get different gates:
  //   /lease_deeds + /lease_deed_tracts -> [BrokerAgent, Landman, LandTech]
  //                                        (`record.edit`)
  //   /lease_deed_recordings            -> [Landman, LandTech]
  //                                        (`recording.record`)
  // A BD.BrokerAgent may maintain tracts but not file a recording.
  const perms = usePermissions();
  const { can } = perms;
  const mayEditRecords = can("record.edit");
  const mayRecord = can("recording.record");
  // An EXTERNAL broker agent: BD.BrokerAgent without any cross-brokerage
  // visibility. Same test the deals list uses, so a user holding BrokerAgent
  // alongside an internal role keeps the internal view.
  const isBrokerAgent = isExternalBrokerAgent(perms);

  const [leaseDeeds, setLeaseDeeds] = useState([]);
  const [leaseDeedMeta, setLeaseDeedMeta] = useState([]);
  const [recordings, setRecordings] = useState([]);
  const [recordingMeta, setRecordingMeta] = useState([]);
  const [tracts, setTracts] = useState([]);
  const [tractMeta, setTractMeta] = useState([]);

  const [selectedId, setSelectedId] = useState(null);
  const [loadingTop, setLoadingTop] = useState(true);
  const [loadingChildren, setLoadingChildren] = useState(false);
  const [errMsg, setErrMsg] = useState(null);

  // Scope toggle for the sub-grids: "selected" = today's editable
  // master/detail scoped to the active lease/deed; "deal" = read-only roll-up
  // of every recording + tract across the whole deal. Deal-wide data is
  // fetched lazily (only when the toggle flips to "deal").
  const [childScope, setChildScope] = useState("selected");
  const [dealRecordings, setDealRecordings] = useState([]);
  const [dealTracts, setDealTracts] = useState([]);
  const [loadingDealWide, setLoadingDealWide] = useState(false);

  // Master-table edit drawer state. `editingLeaseDeed === null` => create.
  const [editOpen, setEditOpen] = useState(false);
  const [editingLeaseDeed, setEditingLeaseDeed] = useState(null);
  // The same drawer, opened read-only for a role that may not write the row.
  // Every role can at least READ a lease/deed; without this the record was
  // only as legible as the grid scrolled.
  const [viewingLeaseDeed, setViewingLeaseDeed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Save failures render INSIDE the drawer. The drawer is full-width and stays
  // open when the save is rejected, so a save error routed to the tab-level
  // Alert sits behind it — the user sees nothing and assumes the save worked.
  const [saveErr, setSaveErr] = useState(null);
  const [form] = Form.useForm();

  const getAuth = useCallback(async () => {
    const userOid = user?.idTokenClaims?.oid || user?.localAccountId;
    let tk = token;
    if (!tk || isTokenExpired(tk)) tk = await getToken();
    return { token: tk, userOid };
  }, [token, getToken, isTokenExpired, user]);

  // Load lease_deeds + all three metadata blobs once per deal. We fetch the
  // sub-grid metadata up front so the modals are ready the instant the user
  // picks an active row.
  const refreshTop = useCallback(async () => {
    if (!dealId) return;
    try {
      setLoadingTop(true);
      setErrMsg(null);
      const { token: tk, userOid } = await getAuth();
      const [listResp, ldMetaResp, recMetaResp, trMetaResp] = await Promise.all([
        dealsService.listLeaseDeeds({ token: tk, userOid, dealId }),
        dealsService.getMetadata({ token: tk, userOid, tableName: "lease_deeds" }),
        dealsService.getMetadata({ token: tk, userOid, tableName: "lease_deed_recordings" }),
        dealsService.getMetadata({ token: tk, userOid, tableName: "lease_deed_tracts" }),
      ]);
      const rows = listResp?.data?.data || [];
      setLeaseDeeds(rows);
      setLeaseDeedMeta(ldMetaResp?.data?.data || []);
      setRecordingMeta(recMetaResp?.data?.data || []);
      setTractMeta(trMetaResp?.data?.data || []);
      // Auto-select the first row if nothing is selected yet, or if the
      // previously selected row vanished (e.g. after a delete).
      setSelectedId((prev) => {
        if (prev && rows.some((r) => r.id === prev)) return prev;
        return rows[0]?.id ?? null;
      });
    } catch (err) {
      console.error("LeaseDeedsTab top load failed:", err);
      setErrMsg(apiErrorText(err));
    } finally {
      setLoadingTop(false);
    }
  }, [dealId, getAuth]);

  const refreshChildren = useCallback(async () => {
    if (!selectedId) {
      setRecordings([]);
      setTracts([]);
      return;
    }
    try {
      setLoadingChildren(true);
      const { token: tk, userOid } = await getAuth();
      const [recResp, trResp] = await Promise.all([
        dealsService.listLeaseDeedRecordings({
          token: tk,
          userOid,
          leaseDeedId: selectedId,
        }),
        dealsService.listLeaseDeedTracts({
          token: tk,
          userOid,
          leaseDeedId: selectedId,
        }),
      ]);
      setRecordings(recResp?.data?.data || []);
      setTracts(trResp?.data?.data || []);
    } catch (err) {
      console.error("LeaseDeedsTab children load failed:", err);
      setErrMsg(apiErrorText(err));
    } finally {
      setLoadingChildren(false);
    }
  }, [selectedId, getAuth]);

  // Deal-wide roll-up: fan out over ALL lease/deeds (already loaded in state)
  // to fetch every recording + every tract, tagging each row with its
  // lease/deed label. Read-only — editing stays in "selected" mode.
  const refreshDealWide = useCallback(async () => {
    try {
      setLoadingDealWide(true);
      const { token: tk, userOid } = await getAuth();
      const [recs, trs] = await Promise.all([
        fanOutChildren({
          masters: leaseDeeds,
          fetchChild: (ld) =>
            dealsService.listLeaseDeedRecordings({
              token: tk,
              userOid,
              leaseDeedId: ld.id,
            }),
          labelOf: leaseDeedLabel,
        }),
        fanOutChildren({
          masters: leaseDeeds,
          fetchChild: (ld) =>
            dealsService.listLeaseDeedTracts({
              token: tk,
              userOid,
              leaseDeedId: ld.id,
            }),
          labelOf: leaseDeedLabel,
        }),
      ]);
      setDealRecordings(recs);
      setDealTracts(trs);
    } catch (err) {
      console.error("LeaseDeedsTab deal-wide load failed:", err);
      setErrMsg(apiErrorText(err));
    } finally {
      setLoadingDealWide(false);
    }
  }, [leaseDeeds, getAuth]);

  useEffect(() => {
    refreshTop();
  }, [refreshTop]);

  useEffect(() => {
    refreshChildren();
  }, [refreshChildren]);

  // Lazy: only fan out the deal-wide data while the toggle is on "Whole deal".
  useEffect(() => {
    if (childScope === "deal") refreshDealWide();
  }, [childScope, refreshDealWide]);

  // `applyFieldEditability` folds the server's per-caller `editable_for_me`
  // into `editable`, so a column this role doesn't own drops out of the edit
  // form while staying visible in the grid. Two reserved columns here:
  // `lease_integrated_date` is owned by BD.LeaseRecords, and
  // `land_system_lease_id` is owned by the land team (the field inventory marks
  // it internal, so a BD.BrokerAgent may read it but not write it). Whoever
  // doesn't own one sees the value, read-only, instead of an input whose save
  // 403s.
  //
  // `lease_cost` gets the harsher treatment: dropped from the metadata
  // outright for an external brokerage, so it leaves the grid and the drawer
  // instead of rendering greyed out.
  const hiddenLeaseDeedFields = useMemo(
    () =>
      isBrokerAgent
        ? new Set([
            ...HIDDEN_LEASE_DEED_FIELDS,
            ...BROKER_HIDDEN_LEASE_DEED_FIELDS,
          ])
        : HIDDEN_LEASE_DEED_FIELDS,
    [isBrokerAgent]
  );
  const filteredLeaseDeedMeta = useMemo(
    () =>
      applyFieldEditability(leaseDeedMeta).filter(
        (m) => !hiddenLeaseDeedFields.has(m.field)
      ),
    [leaseDeedMeta, hiddenLeaseDeedFields]
  );
  const filteredRecordingMeta = useMemo(
    () => (recordingMeta || []).filter((m) => !HIDDEN_RECORDING_FIELDS.has(m.field)),
    [recordingMeta]
  );
  const filteredTractMeta = useMemo(
    () => (tractMeta || []).filter((m) => !HIDDEN_TRACT_FIELDS.has(m.field)),
    [tractMeta]
  );

  // Injects the full-width share-bounds banner at the top of the tract form's
  // Terms section (lessor_royalty / other_burdens / warwick_orri) — see
  // withShareBoundsNotice for why this goes through the metadata.
  const tractPanelProps = useMemo(
    () =>
      withShareBoundsNotice({
        metadata: filteredTractMeta,
        customRenderers: {
          county: countyFieldRenderer,
          // township / range / section as lookups against the survey grid —
          // the same cascade county belongs to.
          ...TRS_FIELD_RENDERERS,
          ...INTEREST_FIELD_RENDERERS,
        },
      }),
    [filteredTractMeta]
  );

  // Lease/Deed CRUD handlers — refresh master list afterwards. Edits or
  // deletes that affect the active row are handled by `refreshTop`'s
  // auto-select fallback.
  const onCreateLeaseDeed = async (body) => {
    const { token: tk, userOid } = await getAuth();
    const resp = await dealsService.createLeaseDeed({
      token: tk,
      userOid,
      body: { ...body, deal_id: dealId },
    });
    const created = resp?.data?.data;
    await refreshTop();
    if (created?.id) setSelectedId(created.id);
    return resp;
  };

  const onUpdateLeaseDeed = async (id, body) => {
    const { token: tk, userOid } = await getAuth();
    const resp = await dealsService.updateLeaseDeed({
      token: tk,
      userOid,
      id,
      body: stripReadOnlyForUpdate(body),
    });
    await refreshTop();
    return resp;
  };

  const onDeleteLeaseDeed = async (id) => {
    const { token: tk, userOid } = await getAuth();
    await dealsService.deleteLeaseDeed({ token: tk, userOid, id });
    if (selectedId === id) setSelectedId(null);
    await refreshTop();
    // The delete cascades to this lease/deed's tracts, so the deal's rolled-up
    // net acres just dropped by their sum.
    await onDealDerivedChange?.();
  };

  // Recording handlers — scoped to the selected lease/deed.
  const onCreateRecording = async (body) => {
    const { token: tk, userOid } = await getAuth();
    const resp = await dealsService.createLeaseDeedRecording({
      token: tk,
      userOid,
      body: { ...body, lease_deed_id: selectedId },
    });
    await refreshChildren();
    return resp;
  };
  const onUpdateRecording = async (id, body) => {
    const { token: tk, userOid } = await getAuth();
    const resp = await dealsService.updateLeaseDeedRecording({
      token: tk,
      userOid,
      id,
      body: stripReadOnlyForUpdate(body),
    });
    await refreshChildren();
    return resp;
  };
  const onDeleteRecording = async (id) => {
    const { token: tk, userOid } = await getAuth();
    await dealsService.deleteLeaseDeedRecording({ token: tk, userOid, id });
    await refreshChildren();
  };

  // Tract handlers. Every one of them changes the deal's derived net acres, so
  // each refreshes the parent deal row as well as this tab's sub-grids.
  const onCreateTract = async (body) => {
    const { token: tk, userOid } = await getAuth();
    const resp = await dealsService.createLeaseDeedTract({
      token: tk,
      userOid,
      body: { ...body, lease_deed_id: selectedId },
    });
    await refreshChildren();
    await onDealDerivedChange?.();
    return resp;
  };
  const onUpdateTract = async (id, body) => {
    const { token: tk, userOid } = await getAuth();
    const resp = await dealsService.updateLeaseDeedTract({
      token: tk,
      userOid,
      id,
      body: stripReadOnlyForUpdate(body),
    });
    await refreshChildren();
    await onDealDerivedChange?.();
    return resp;
  };
  const onDeleteTract = async (id) => {
    const { token: tk, userOid } = await getAuth();
    await dealsService.deleteLeaseDeedTract({ token: tk, userOid, id });
    await refreshChildren();
    await onDealDerivedChange?.();
  };

  // --- Master table (lease_deeds) --------------------------------------------
  const openCreate = () => {
    setEditingLeaseDeed(null);
    setViewingLeaseDeed(false);
    setSaveErr(null);
    form.resetFields();
    setEditOpen(true);
  };
  const openEdit = (record) => {
    setEditingLeaseDeed(record);
    setViewingLeaseDeed(false);
    setSaveErr(null);
    setEditOpen(true);
  };
  const openView = (record) => {
    setEditingLeaseDeed(record);
    setViewingLeaseDeed(true);
    setSaveErr(null);
    setEditOpen(true);
  };
  const closeEdit = () => {
    setEditOpen(false);
    setEditingLeaseDeed(null);
    setViewingLeaseDeed(false);
    setSaveErr(null);
  };

  // Columns the CURATED metadata marks non-editable — computed / derived, not
  // user input for anyone. Read off the RAW metadata, because
  // applyFieldEditability flattens `editable_for_me: false` into the same
  // `editable: false`, and the two need opposite treatment: a role-gated
  // column is shown disabled, a computed one stays out of the form.
  const curatedReadOnlyFields = useMemo(
    () =>
      new Set(
        (Array.isArray(leaseDeedMeta) ? leaseDeedMeta : [])
          .filter((m) => m?.editable === false)
          .map((m) => m.field)
      ),
    [leaseDeedMeta]
  );

  // Field set for the drawer form: visible, non-audit, non-computed, with
  // parent-driven columns stripped. Deliberately does NOT filter on the
  // per-role flag — `disableNonEditable` greys those out instead, so the form
  // shows the whole lease/deed. Filtering them left BD.LeaseRecords looking at
  // a drawer containing one date picker and nothing identifying the lease.
  const editFormFilter = useCallback(
    (row) =>
      !row.hide &&
      !SERVER_MANAGED_FIELDS.has(row.field) &&
      !hiddenLeaseDeedFields.has(row.field) &&
      !curatedReadOnlyFields.has(row.field),
    [curatedReadOnlyFields, hiddenLeaseDeedFields]
  );

  // Fires only on a user edit, never on mount or `setFieldsValue` below — so
  // this cannot loop, and loading a lease never rewrites its stored dates.
  const onFormValuesChange = useCallback(
    (changed, all) => {
      const patch = deriveLeaseDates(changed, all);
      if (Object.keys(patch).length) form.setFieldsValue(patch);
    },
    [form]
  );

  const onSubmitEdit = async () => {
    setSaveErr(null);
    try {
      const values = await form.validateFields();
      // The reserved columns render disabled rather than filtered, so the
      // form still holds them and `fromFormValues` turns an empty date into an
      // explicit `null`. POST /lease_deeds judges every key the body carries,
      // so that echo 403s the create naming `lease_integrated_date` — a field
      // the user could see was greyed out. Strip what this role can't write.
      const body = stripNonWritableFields(
        fromFormValues(values, leaseDeedMeta),
        leaseDeedMeta
      );
      setSubmitting(true);
      if (editingLeaseDeed) {
        await onUpdateLeaseDeed(editingLeaseDeed.id, body);
      } else {
        await onCreateLeaseDeed(body);
      }
      closeEdit();
    } catch (err) {
      if (err?.errorFields) return; // AntD validation — keep drawer open
      console.error("Lease/Deed save failed:", err);
      // Pass the metadata so a 422 names the form's own labels
      // ("Lessor/Grantor is required") rather than the column names.
      setSaveErr(
        saveErrorText(
          err,
          editingLeaseDeed ? "save the lease/deed" : "add the lease/deed",
          { metadata: leaseDeedMeta }
        )
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Columns = metadata-driven grid columns + a trailing Edit/Delete action
  // column. metadataToAntdColumns is the same builder WigMetadataCrudPanel
  // uses internally, so the grid matches the other tabs.
  const leaseDeedColumns = useMemo(() => {
    // metadataToAntdColumns (library) doesn't know our field-aware formats, so
    // post-process money/acreage/interest columns (lease_cost, extension_option_bpa).
    const cols = applyNumericFormatters(
      metadataToAntdColumns(filteredLeaseDeedMeta),
      filteredLeaseDeedMeta
    );
    // Leading Edit action, mirroring the "View" button on the deals-list
    // table (size="small" type="link" icon + label). Row-click selects the
    // active lease/deed; the Edit button opens the full-screen drawer.
    const editCol = {
      title: "",
      key: "_edit",
      fixed: "left",
      width: 56,
      align: "center",
      onCell: () => ({ onClick: (ev) => ev.stopPropagation() }),
      render: (_v, record) => (
        <Button
          size="small"
          type="link"
          icon={<EditOutlined />}
          onClick={() => openEdit(record)}
          aria-label={`Edit lease/deed ${leaseDeedLabel(record)}`}
        />
      ),
    };
    // Paperclip -> the Attachments tab, already scoped to THIS lease/deed.
    // Attaching below the deal level was reachable only through the
    // Attachments tab’s own parent-type radio + entity select, which UAT
    // testers never found (reported as "upload is limited to the Deal parent
    // type"). Ungated: viewing and downloading attachments is open to every
    // role, and the uploader inside the tab stays gated on
    // `attachments.upload`.
    const attachCol = {
      title: "",
      key: "__attachments__",
      fixed: "right",
      width: 60,
      align: "center",
      onCell: () => ({ onClick: (ev) => ev.stopPropagation() }),
      render: (_v, record) => (
        <AttachmentsScopeButton
          parentTable="lease_deed"
          parentId={record.id}
          iconOnly
          tooltip={`Attachments for lease/deed ${leaseDeedLabel(record)}`}
        />
      ),
    };
    const deleteCol = {
      title: "",
      key: "__delete__",
      fixed: "right",
      width: 60,
      align: "center",
      onCell: () => ({ onClick: (ev) => ev.stopPropagation() }),
      render: (_v, record) => (
        <Popconfirm
          title="Delete lease/deed?"
          description="This cascades to its recordings and tracts."
          okText="Delete"
          okButtonProps={{ danger: true }}
          onConfirm={() => onDeleteLeaseDeed(record.id)}
        >
          <Button
            type="text"
            size="small"
            danger
            icon={<DeleteOutlined />}
            aria-label="Delete lease/deed"
          />
        </Popconfirm>
      ),
    };
    // The lease/deed grid is hand-rolled rather than a CrudPanel, so the
    // row actions are gated here, and the two gates differ.
    //
    // EDIT follows the metadata, not `record.edit`: BD.LeaseRecords owns
    // `lease_integrated_date` and is admitted to PUT /lease_deeds solely to
    // stamp it, but holds no `record.edit`. Gating the button on that action
    // hid the only route to the field — the role could see the lease and never
    // record the handoff. Same reasoning as the deal-level Edit button. The
    // drawer is already per-field (`editFormFilter`), so a role that gets in
    // this way sees just the column it owns.
    //
    // DELETE stays on `record.edit`: stamping a date is not licence to destroy
    // a lease, which mirrors the server (LeaseRecords is on the PUT endpoint
    // but not POST/DELETE).
    // `editable_for_me === true` is the explicit test on purpose: an older
    // de-hub omits the flag entirely, and `editable !== false` would then read
    // as "everyone may edit" and hand every role a button that 403s on save.
    const ownsAColumn = filteredLeaseDeedMeta.some(
      (m) => m?.editable_for_me === true && editFormFilter(m)
    );
    // A role with no writable column still gets the record, read-only —
    // matching `allowView` on the library's CrudPanel grids.
    if (!mayEditRecords && !ownsAColumn) {
      const viewCol = {
        ...editCol,
        key: "_view",
        render: (_v, record) => (
          <Button
            size="small"
            type="link"
            icon={<EyeOutlined />}
            onClick={() => openView(record)}
            aria-label={`View lease/deed ${leaseDeedLabel(record)}`}
          />
        ),
      };
      return [viewCol, ...cols, attachCol];
    }
    return mayEditRecords
      ? [editCol, ...cols, attachCol, deleteCol]
      : [editCol, ...cols, attachCol];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredLeaseDeedMeta, mayEditRecords, editFormFilter]);

  const noSelection = !selectedId;
  const activeLabel = leaseDeedLabel(leaseDeeds.find((l) => l.id === selectedId));
  const placeholderCard = (entityLabel) => (
    <Card size="small">
      <Empty
        description={
          <Text type="secondary">
            Select a lease/deed above to manage its {entityLabel}.
          </Text>
        }
      />
    </Card>
  );

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }} className="wig-multi-table-tab">
      {errMsg && (
        <Alert
          type="error"
          showIcon
          message="Failed to load lease/deeds"
          description={errMsg}
        />
      )}

      {/* View-scope toggle — pinned at the top so it governs the whole tab. */}
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 10,
          padding: "4px 10px",
          borderRadius: 6,
          background:
            "var(--ant-color-primary-bg-hover, rgba(22, 119, 255, 0.2))",
        }}
      >
        <Text strong style={{ fontSize: 14 }}>
          Show:
        </Text>
        <Segmented
          value={childScope}
          onChange={setChildScope}
          options={[
            { label: "Selected lease/deed", value: "selected" },
            { label: "Whole deal", value: "deal" },
          ]}
        />
      </div>

      <Card
        size="small"
        title={`Lease/Deeds (${leaseDeeds.length})`}
        extra={
          mayEditRecords ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate} disabled={loadingTop}>
              Add
            </Button>
          ) : null
        }
      >
        <WigAntTable
          size="small"
          rowKey="id"
          dataSource={leaseDeeds}
          columns={leaseDeedColumns}
          loading={loadingTop}
          showSearch={false}
          showExport={false}
          showActionButton={false}
          pagination={{ size: "small", hideOnSinglePage: true }}
          scroll={{ x: "max-content", y: 320 }}
          locale={{ emptyText: "No lease/deeds on this deal yet" }}
          rowClassName={(r) => (r.id === selectedId ? "wig-active-row" : "")}
          onRow={(record) => ({
            onClick: () => setSelectedId(record.id),
            style: { cursor: "pointer" },
          })}
        />
      </Card>

      {childScope === "deal" ? (
        <>
          <Card size="small" title={`Recordings — whole deal (${dealRecordings.length})`}>
            <ReadOnlyChildTable
              parentHeader="Lease/Deed"
              rows={dealRecordings}
              metadata={recordingMeta}
              tableName="lease_deed_recordings"
              loading={loadingDealWide}
              emptyText="No recordings on this deal"
            />
          </Card>
          <Card size="small" title={`Tracts — whole deal (${dealTracts.length})`}>
            <ReadOnlyChildTable
              parentHeader="Lease/Deed"
              rows={dealTracts}
              metadata={tractMeta}
              tableName="lease_deed_tracts"
              loading={loadingDealWide}
              emptyText="No tracts on this deal"
            />
          </Card>
        </>
      ) : (
        <>
          {noSelection ? (
            placeholderCard("recordings")
          ) : (
            <Card size="small" title={`Recordings for ${activeLabel} (${recordings.length})`}>
              <WigMetadataCrudPanel
                title="Recordings"
                rows={recordings}
                metadata={filteredRecordingMeta}
                loading={loadingChildren}
                allowView
                disableNonEditable
                onCreate={mayRecord ? onCreateRecording : undefined}
                onUpdate={mayRecord ? onUpdateRecording : undefined}
                onDelete={mayRecord ? onDeleteRecording : undefined}
                customRenderers={{ county: countyFieldRenderer }}
                newRowDefaults={{ lease_deed_id: selectedId }}
                emptyText="No recordings on this lease/deed yet"
                editTrigger="button"
                editLayout="wide"
                height={260}
              />
            </Card>
          )}

          {noSelection ? (
            placeholderCard("tracts")
          ) : (
            <Card
              size="small"
              title={`Tracts for ${activeLabel} (${tracts.length})`}
              extra={
                <AttachmentsScopeButton
                  parentTable="lease_deed_tract"
                  tooltip="Attach files to a tract on this deal"
                />
              }
            >
              <WigMetadataCrudPanel
                title="Tracts"
                rows={tracts}
                loading={loadingChildren}
                allowView
                disableNonEditable
                onCreate={mayEditRecords ? onCreateTract : undefined}
                onUpdate={mayEditRecords ? onUpdateTract : undefined}
                onDelete={mayEditRecords ? onDeleteTract : undefined}
                cellRenderers={numericCellRenderers(filteredTractMeta)}
                // metadata / customRenderers / formFieldFilter: county cascade +
                // the 0–1 burden guards + the section banner.
                {...tractPanelProps}
                newRowDefaults={{ lease_deed_id: selectedId }}
                emptyText="No tracts on this lease/deed yet"
                editTrigger="button"
                height={260}
                editLayout="fullscreen"
              />
            </Card>
          )}
        </>
      )}

      {/* Full-screen Add/Edit drawer for a lease/deed, composed from the
          library's WigMetadataForm (3-up, grouped by category). */}
      <Drawer
        title={
          viewingLeaseDeed
            ? "Lease/Deed"
            : editingLeaseDeed
              ? "Edit Lease/Deed"
              : "Add Lease/Deed"
        }
        open={editOpen}
        onClose={closeEdit}
        styles={{ wrapper: { width: "100%" } }}
        destroyOnHidden
        footer={
          <Space style={{ width: "100%", justifyContent: "flex-end" }}>
            <Button onClick={closeEdit}>
              {viewingLeaseDeed ? "Close" : "Cancel"}
            </Button>
            {!viewingLeaseDeed && (
              <Button type="primary" loading={submitting} onClick={onSubmitEdit}>
                {editingLeaseDeed ? "Save" : "Create"}
              </Button>
            )}
          </Space>
        }
      >
        {saveErr && (
          <Alert
            type="error"
            showIcon
            closable
            message={saveErr}
            onClose={() => setSaveErr(null)}
            style={{ marginBottom: 16 }}
          />
        )}
        {/* Both modes show the whole lease/deed. A view disables the entire
            Form; an edit disables only the columns this role can't write
            (`disableNonEditable` reads the `editable` flag that
            applyFieldEditability folded `editable_for_me` into). */}
        {/* The form works out two of its own dates (feedback #18, #17): the
            effective date defaults to the document date while it is still
            empty, and the expiration recalculates from effective + term. Both
            are defaults — `onValuesChange` only fires on a user edit, so
            opening an existing lease never rewrites it, and typing over either
            result sticks. */}
        <Form
          form={form}
          layout="vertical"
          preserve={false}
          disabled={viewingLeaseDeed}
          onValuesChange={onFormValuesChange}
        >
          <WigMetadataForm
            metadata={filteredLeaseDeedMeta}
            initialValues={editingLeaseDeed || { deal_id: dealId }}
            fieldFilter={viewingLeaseDeed ? (row) => !row.hide : editFormFilter}
            disableNonEditable={!viewingLeaseDeed}
            groupByCategory
            columns={3}
            form={form}
          />
        </Form>
      </Drawer>
    </Space>
  );
};

export default LeaseDeedsTab;
